'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import useReducedMotion from '@alltrails/core/hooks/useReducedMotion';
import IconButton from '@alltrails/denali/components/IconButton';
import PagedProgressBar from '@alltrails/denali/components/PagedProgressBar';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import type { ConditionStyleId } from '@alltrails/maps/types/Styles';
import TrailWeatherOverviewBackgroundTheme from '../../types/TrailWeatherOverviewBackgroundTheme';
import ConditionsWeatherMapButton from '../ConditionsWeatherMapButton/ConditionsWeatherMapButton';
import styles from './ConditionsUpsellCard.module.scss';
import { lottieDurationMs } from './constants';
import StaticImage from './StaticImage';

const UpsellLottie = dynamic(() => import('./UpsellLottie'), { ssr: false });

type ConditionsUpsellCardProps = {
  /**
   * When true, forces the compact (mobile-style) layout regardless of viewport.
   */
  compact?: boolean;
  initialTheme: TrailWeatherOverviewBackgroundTheme;
  onShowUpsellModal: () => void;
  onWeatherMapClick: (condition: ConditionStyleId, index: number) => void;
};

const ConditionsUpsellCard = ({ compact, initialTheme, onShowUpsellModal, onWeatherMapClick }: ConditionsUpsellCardProps) => {
  const intl = useIntl();
  const screenIsMobile = useIsMobileSizedScreen();
  const isMobile = compact ?? screenIsMobile;
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(containerRef);
  const prefersReducedMotion = useReducedMotion();
  const [showLottie, setShowLottie] = useState(false);

  useEffect(() => {
    if (isVisible && !prefersReducedMotion) {
      setShowLottie(true);
    }
  }, [isVisible, prefersReducedMotion]);

  const conditions = useMemo(() => {
    let conditionOrder: ConditionStyleId[] = ['temperature', 'precipitation', 'snowDepth'];
    if (initialTheme.includes('rainy')) {
      conditionOrder = ['precipitation', 'temperature', 'snowDepth'];
    }
    conditionOrder.push('groundConditions');
    return conditionOrder;
  }, [initialTheme]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeCondition = conditions[activeIndex];

  const advanceCondition = useCallback(() => {
    setActiveIndex(i => (i + 1) % conditions.length);
  }, [conditions.length]);

  const title = useMemo(() => {
    switch (activeCondition) {
      case 'groundConditions':
        return intl.formatMessage({ defaultMessage: 'Is it muddy? Icy? Dry?' });
      case 'precipitation':
        return intl.formatMessage({ defaultMessage: 'When and where will it rain?' });
      case 'snowDepth':
        return intl.formatMessage({ defaultMessage: 'Is there snow on the trail?' });
      case 'temperature':
        return intl.formatMessage({ defaultMessage: 'Where will it be warmest?' });
      default:
        return '';
    }
  }, [activeCondition, intl]);

  const onMapButtonClick = useCallback(() => {
    onWeatherMapClick(activeCondition, activeIndex);
    onShowUpsellModal();
  }, [activeCondition, activeIndex, onShowUpsellModal, onWeatherMapClick]);

  const contents = (
    <>
      <PagedProgressBar
        className={styles.progressBar}
        index={activeIndex}
        total={conditions.length}
        stepDurationMs={showLottie ? lottieDurationMs : 0}
      />
      <div className={styles.heading}>
        <FormattedMessage defaultMessage="Trail conditions" />

        <PlusLogoBadge size="sm" textColor="dark" variant="full" />
      </div>
      {showLottie ? <UpsellLottie condition={activeCondition} onEnd={advanceCondition} /> : <StaticImage condition={activeCondition} />}
      <div className={styles.title}>{title}</div>
      <div className={styles.description}>{intl.formatMessage({ defaultMessage: 'Get hourly trail conditions for the week ahead' })}</div>
    </>
  );

  return (
    <div className={classNames(styles.container, styles[activeCondition])} ref={containerRef}>
      {isMobile ? (
        <button aria-label={intl.formatMessage({ defaultMessage: 'Next' })} className={styles.content} onClick={advanceCondition}>
          {contents}
        </button>
      ) : (
        <div className={styles.content}>
          {contents}
          <IconButton
            className={styles.nextButton}
            icon={{ Component: ArrowSq, orientation: 'right' }}
            onClick={advanceCondition}
            title={intl.formatMessage({ defaultMessage: 'Next' })}
            size="md"
          />
        </div>
      )}
      <ConditionsWeatherMapButton className={styles.mapButton} condition={conditions[activeIndex]} onClick={onMapButtonClick} />
    </div>
  );
};

export default ConditionsUpsellCard;

export const ConditionsUpsellCardLoading = () => {
  return <ShimmerContainer className={classNames(styles.container, styles.loading)} />;
};
